import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import BottomDrawer from '../../components/common/BottomDrawer';
import { InputBackOffice } from '../../components/common/InputBackoffice';
import Spinner from '../../components/common/Spinner';
import { ErrorToast } from '../../components/common/Toasts/Toasts';
import check from '../../assets/icons/check.svg';
import search from '../../assets/icons/search.svg';
import { P2, P3 } from '../../styles/FontStyles';
import {
  ArrowPolygon,
  AuthLayoutTitle,
  Select,
  ToggleTabButton,
  ToggleTabs
} from '../../styles/GlobalComponents';
import {
  FlexCenter,
  Label,
  SInput,
  SInputContainer
} from '../../styles/stylesStyled';
import { CreationTypes } from '../../types/loginTypes';
import { RouteComponentProps } from 'react-router';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { Button } from '../../components';
import countries from '../../lib/countries';
import { colors } from '../../styles/GlobalStyles';
import {
  updateProfileDetails,
  updateSocialProfileDetails
} from './ProfileDetails.actions';

const ProfileDetails = ({ history }: RouteComponentProps) => {
  const { t } = useTranslation('onboarding');

  const dispatch = useAppDispatch();

  const { loading, register } = useAppSelector((state) => state.auth);

  const { user, token } = register;

  const [name, setName] = useState('');
  const [username, setUsername] = useState('');
  const [surname, setSurname] = useState('');
  const [genderType, setGenderType] = useState(1);
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [postal, setPostal] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [country, setCountry] = useState('');
  const [userCountry] = useState('');
  const [countryCode, setCountryCode] = useState('');

  const [countryList, setCountryList] = useState(countries);
  const [countryImage, setCountryImage] = useState('');

  const [selectedTab, setSelectedTab] = useState('male');

  const [isVisible, setIsVisible] = useState(false);

  const letterUpercaseAndSpace = /^[a-zA-Z\s]*$/;

  const onClose = useCallback(() => {
    setIsVisible(false);
  }, []);

  const selectTab = (tab: string) => {
    setSelectedTab(tab);
    if (tab === 'male') {
      setGenderType(1);
    } else if (tab === 'female') {
      setGenderType(2);
    } else {
      setGenderType(3);
    }
  };

  const confirm = () => {
    if (!name) {
      ErrorToast(t('profile_details.err_please_add_name'));
    } else if (!surname) {
      ErrorToast(t('profile_details.err_please_add_lastname'));
    } else if (!username) {
      ErrorToast(t('profile_details.err_please_add_username'));
    } else if (!address) {
      ErrorToast(t('profile_details.err_please_add_address'));
    } else if (!postal) {
      ErrorToast(t('profile_details.err_please_add_postal_code'));
    } else if (!city) {
      ErrorToast(t('profile_details.err_please_add_city'));
    } else {
      if (user.creationType === CreationTypes.Local) {
        dispatch(
          updateProfileDetails({
            userId: user.id,
            name,
            surname,
            username,
            genderType,
            address,
            zipCode: postal,
            city,
            country,
            countryCode,
            history
          })
        );
      } else {
        dispatch(
          updateSocialProfileDetails({
            token,
            name,
            surname,
            username,
            genderType,
            address,
            zipCode: postal,
            city,
            country,
            countryCode,
            history
          })
        );
      }
    }
  };

  const checkFirstName = (e: string) => {
    if (e.match(letterUpercaseAndSpace)) {
      setName(
        e
          .replace(/|[0-9]/g, '')
          .toLowerCase()
          .split('profile_details. ')
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ')
      );
    } else {
      return;
    }
  };

  const checkLastName = (e: string) => {
    if (e.match(letterUpercaseAndSpace)) {
      setSurname(
        e
          .replace(/|[0-9]/g, '')
          .toLowerCase()
          .split('profile_details. ')
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ')
      );
    } else {
      return;
    }
  };

  const checkCountry = (country: any) => {
    setCountryList(
      countryList.map((item) =>
        item.name === country.name
          ? {
              ...item,
              checked: true
            }
          : {
              ...item,
              checked: false
            }
      )
    );
  };

  useEffect(() => {
    setName(user?.name);
    setSurname(user?.surname);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const Required = () => {
    return <span style={{ color: colors.primary900 }}>*</span>;
  };

  return (
    <>
      {loading && <Spinner fullScreen={true} transparent />}

      <AuthLayoutTitle>Welcome to United One</AuthLayoutTitle>

      <P3 center mb='40' color={colors.primary900}>
        {t('profile_details.tell_us_who_you_are')}
      </P3>

      {/* <Label>
        {t('profile_details.select_your_gender')} <Required />
      </Label> */}

      <ToggleTabs>
        <ToggleTabButton
          selected={selectedTab === 'male'}
          onClick={() => selectTab('male')}>
          {t('profile_details.male')}
        </ToggleTabButton>
        <ToggleTabButton
          selected={selectedTab === 'female'}
          onClick={() => selectTab('female')}>
          {t('profile_details.female')}
        </ToggleTabButton>
        <ToggleTabButton
          selected={selectedTab === 'other'}
          onClick={() => selectTab('other')}>
          {t('profile_details.other')}
        </ToggleTabButton>
      </ToggleTabs>

      <Label>
        {t('profile_details.usernameLabel')}
        <Required />
      </Label>

      <InputBackOffice
        id={'username'}
        type={'text'}
        placeholder={t('profile_details.usernamePlaceholder')}
        name={'username'}
        value={username.replace(/[^A-Z0-9]/gi, '') || ''}
        onChange={(e: string) =>
          setUsername(e.replace(/[^A-Z0-9]/gi, '').toLowerCase())
        }
        label={t('profile_details.usernameLabel')}
      />

      <Label>
        {t('profile_details.firstAndLastName')}
        <Required />
      </Label>

      <InputBackOffice
        id={'firstName'}
        type={'text'}
        placeholder={t('profile_details.firstNamePlaceholder')}
        name={'name'}
        value={name}
        onChange={(e: string) => checkFirstName(e)}
        label={t('profile_details.firstNameLabel')}
        maxlength='16'
      />

      <InputBackOffice
        id={'surname'}
        type={'text'}
        placeholder={t('profile_details.lastNamePlaceholder')}
        name={'surname'}
        value={surname}
        onChange={(e: string) => checkLastName(e)}
        label={t('profile_details.lastNameLabel')}
        maxlength='16'
      />

      <Label>
        {t('profile_details.address')}
        <Required />
      </Label>
      <InputBackOffice
        id={'address'}
        type={'text'}
        placeholder={t('profile_details.streetAddressPlaceholder')}
        name={'address'}
        value={address || ''}
        onChange={setAddress}
        label={t('profile_details.streetAddressLabel')}
      />

      <InputBackOffice
        id={'postal'}
        type={'text'}
        placeholder={t('profile_details.postalCodePlaceholder')}
        name={'postal'}
        value={postal || ''}
        onChange={setPostal}
        label={t('profile_details.postalCodeLabel')}
      />

      <InputBackOffice
        id={'city'}
        type={'text'}
        placeholder={t('profile_details.cityPlaceholder')}
        name={'city'}
        value={city || ''}
        onChange={setCity}
        label={t('profile_details.cityLabel')}
      />

      <div>
        <Label>
          Country <Required />
        </Label>

        <Select
          onClick={() => {
            setIsVisible(true);
          }}
          style={{ cursor: 'pointer' }}>
          <FlexCenter>
            {countryImage && (
              <img
                src={countryImage}
                alt='flag'
                style={{
                  marginRight: '20px',
                  width: '28px',
                  height: '20px'
                }}
              />
            )}

            <div>{country || t('profile_details.countryLabel')}</div>
          </FlexCenter>
          <ArrowPolygon direction={''} />
        </Select>
      </div>

      <Button
        negative
        m='30px 0 10px 0'
        disabled={
          name === '' ||
          username === '' ||
          surname === '' ||
          address === '' ||
          postal === '' ||
          city === ''
        }
        onClick={confirm}>
        {t('profile_details.continue')}
      </Button>

      <BottomDrawer isVisible={isVisible} onClose={onClose} height={'90vh'}>
        <SInputContainer>
          <img src={search} alt='search.svg' />
          <SInput
            placeholder={t('profile_details.countryLabelPlaceholder')}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </SInputContainer>
        {countryList &&
          countryList
            ?.sort((a) => (a.name === userCountry ? -1 : 1))
            .filter((value) => {
              if (searchTerm === '') {
                return value;
              } else if (
                value.name.toLowerCase().includes(searchTerm.toLowerCase())
              ) {
                return value;
              } else return null;
            })
            .map((country, i) => {
              if (country.hidden) return null;
              return (
                <div
                  onClick={() => {
                    setCountry(country.name);
                    setCountryCode(country.code);
                    setCountryImage(country.flag);
                    setIsVisible(false);
                    checkCountry(country);
                  }}
                  key={i}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    cursor: 'pointer'
                  }}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      marginBottom: '20px'
                    }}>
                    <img
                      src={country.flag}
                      alt='flag'
                      style={{
                        marginRight: '20px',
                        width: '28px',
                        height: '20px'
                      }}
                    />

                    <P2>{country.name}</P2>
                  </div>
                  {country.checked && (
                    <img
                      src={check}
                      alt='check'
                      style={{ marginBottom: '30px' }}
                    />
                  )}
                </div>
              );
            })}
      </BottomDrawer>
    </>
  );
};

export default ProfileDetails;
